import type {DeepPartial} from '@/global';
import type {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {ExampleImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {scrollToTopOfElement} from '@Utils/dom.util';
import {AI_CHAT_HOME_SCROLL_ID} from '@/ai-chat/ai-chat.types';
import {InspirationLayoutVariant, InspirationWidthVariant} from './ai-chat-window-inspirations.types';
import type {InspirationData} from './ai-chat-window-inspirations.types';
import styles from './ai-chat-window-inspirations.module.scss';

export const scrollAndUpdatePrompt = (prompt: string, attachments: ExampleImageAttachment[], updatePromptBox: (options: DeepPartial<AiPromptBox>) => void): void => {
  const scrollEl = document.getElementById(AI_CHAT_HOME_SCROLL_ID);
  if (scrollEl) {
    scrollToTopOfElement(scrollEl, () => {
      updatePromptBox({
        prompt,
        promptBoxState: PromptBoxState.ACTIVE,
        attachments: {
          items: attachments,
        },
      });
    });
  } else {
    updatePromptBox({
      prompt,
      promptBoxState: PromptBoxState.ACTIVE,
      attachments: {
        items: attachments,
      },
    });
  }
};

export const aiChatWindowInspirationsData = (updatePromptBox: (options: DeepPartial<AiPromptBox>) => void): InspirationData[] => {
  return [
    {
      visualLayoutVariant: InspirationLayoutVariant.Centered,
      imageURL: 'ai-chat-inspiration-tile-1-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_design'),
      pillIcon: 'icon-designs',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_1'),
      widthVariant: InspirationWidthVariant.Medium,
      imageClasses: styles.firstInspirationImage,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_1'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-2-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_images'),
      pillIcon: 'icon-image',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_2'),
      widthVariant: InspirationWidthVariant.Medium,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_2'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.BottomRightRadius,
      imageURL: 'ai-chat-inspiration-tile-3-image-1-thumb.webp?ts=11',
      imageURLToConvertTo: 'ai-chat-inspiration-tile-3-image-2-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_redesign'),
      pillIcon: 'icon-ai-caption',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_3'),
      widthVariant: InspirationWidthVariant.Narrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(
          window.i18next.t('pmwjs_ai_chat_inspiration_prompt_3'),
          [{type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-3-image-1.webp?ts=11'}],
          updatePromptBox
        );
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.BottomRightRadius,
      imageURL: 'ai-chat-inspiration-tile-4-image-1-thumb.webp?ts=11',
      imageURLToConvertTo: 'ai-chat-inspiration-tile-4-image-2-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_change_background'),
      pillIcon: 'icon-designs',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_4'),
      widthVariant: InspirationWidthVariant.Narrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(
          window.i18next.t('pmwjs_ai_chat_inspiration_prompt_4'),
          [{type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-4-image-1.webp?ts=11'}],
          updatePromptBox
        );
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-5-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_marketing_ideas'),
      pillIcon: 'icon-megaphone',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_5'),
      widthVariant: InspirationWidthVariant.MediumNarrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_5'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-6-image-1-thumb.webp?ts=14',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_logo'),
      pillIcon: 'icon-image',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_6'),
      widthVariant: InspirationWidthVariant.MediumNarrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_6'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-7-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_images'),
      pillIcon: 'icon-image',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_7'),
      widthVariant: InspirationWidthVariant.ExtraNarrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_7'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.BottomLeftRadius,
      imageURL: 'ai-chat-inspiration-tile-8-image-1-thumb.webp?ts=11',
      imageURLToConvertTo: 'ai-chat-inspiration-tile-8-image-2-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_replace_objects'),
      pillIcon: 'icon-ai-caption',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_8'),
      widthVariant: InspirationWidthVariant.Wide,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(
          window.i18next.t('pmwjs_ai_chat_inspiration_prompt_8'),
          [{type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-8-image-1.webp?ts=11'}],
          updatePromptBox
        );
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-9-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_design'),
      pillIcon: 'icon-designs',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_9'),
      widthVariant: InspirationWidthVariant.MediumNarrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_9'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-10-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_marketing_ideas'),
      pillIcon: 'icon-megaphone',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_10'),
      widthVariant: InspirationWidthVariant.Narrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_10'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-11-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_redesign'),
      pillIcon: 'icon-image',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_11'),
      widthVariant: InspirationWidthVariant.Narrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_11'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.BottomRightRadius,
      imageURL: 'ai-chat-inspiration-tile-12-image-1-thumb.webp?ts=11',
      imageURLToConvertTo: 'ai-chat-inspiration-tile-12-image-2-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_fancy_text'),
      pillIcon: 'icon-ai-caption',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_12'),
      widthVariant: InspirationWidthVariant.Medium,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(
          window.i18next.t('pmwjs_ai_chat_inspiration_prompt_12'),
          [{type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-12-image-1.webp?ts=11'}],
          updatePromptBox
        );
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.Centered,
      imageURL: 'ai-chat-inspiration-tile-13-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_design'),
      pillIcon: 'icon-designs',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_13'),
      widthVariant: InspirationWidthVariant.Medium,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_13'), [], updatePromptBox);
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.BottomRightRadius,
      imageURL: 'ai-chat-inspiration-tile-14-image-1-thumb.webp?ts=11',
      imageURLToConvertTo: 'ai-chat-inspiration-tile-14-image-2-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_change_styles'),
      pillIcon: 'icon-ai-caption',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_14'),
      widthVariant: InspirationWidthVariant.Narrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(
          window.i18next.t('pmwjs_ai_chat_inspiration_prompt_14'),
          [{type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-14-image-1.webp?ts=11'}],
          updatePromptBox
        );
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.Centered,
      imageURL: 'ai-chat-inspiration-tile-15-image-1-thumb.webp?ts=11',
      imageURLToConvertTo: 'ai-chat-inspiration-tile-15-image-2-thumb.webp?ts=11',
      attachmentURL: 'ai-chat-inspiration-tile-15-image-3.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_add_object'),
      pillIcon: 'icon-ai-caption',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_15'),
      widthVariant: InspirationWidthVariant.Narrow,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(
          window.i18next.t('pmwjs_ai_chat_inspiration_prompt_15'),
          [
            {type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-15-image-1.webp?ts=11'},
            {type: PromptAttachmentType.EXAMPLE_IMAGE, fileName: 'ai-chat-inspiration-tile-15-image-3.webp?ts=11'},
          ],
          updatePromptBox
        );
      },
    },
    {
      visualLayoutVariant: InspirationLayoutVariant.FullWidth,
      imageURL: 'ai-chat-inspiration-tile-16-image-1-thumb.webp?ts=11',
      pillText: window.i18next.t('pmwjs_ai_chat_inspiration_images'),
      pillIcon: 'icon-image',
      prompt: window.i18next.t('pmwjs_ai_chat_inspiration_prompt_16'),
      widthVariant: InspirationWidthVariant.Medium,
      onRecreate: (): void => {
        scrollAndUpdatePrompt(window.i18next.t('pmwjs_ai_chat_inspiration_prompt_16'), [], updatePromptBox);
      },
    },
  ];
};
